/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { useCallback, useState } from "react";
import { useCurrentEditor } from "@tiptap/react";
import debounce from "lodash.debounce";
import { isValidGuid, isStringNotEmpty } from "utils/empty-exists";
import Config from "data/config";
import { Logger } from "cms/utils/log/logger";

export const useUpdateLink = () => {
    
    const markType = "link";
    const { editor } = useCurrentEditor();
    const initialSelection = editor?.view?.state?.selection;
    const initialText = editor?.view?.state?.selection?.node?.text;
    
    const link = editor.getAttributes(markType);
    
    const [selection,setSelection] = useState(initialSelection);
    const [selectedText,setSelectedText] = useState(initialText);
    
    const findSelection = debounce(() => {
        if (!editor) {
            return;
        }
        const { from, to, empty } = editor.state.selection;
        if (empty) {
            return;
        }
        const selectionText = editor.state.doc.textBetween(from, to, " ");
        Logger.debug(selectionText);
        setSelectedText(selectionText);
    }, 900);
    
    const updateLink = useCallback(
        (link) => {
            const guid = link?.guid;
            let replacementText = link?.text;
            const url = link?.url;
            const webpage = link?.webpage;
            const link_type = link?.webpage_type;
            
            let isExternalLink;
            let isInternalLink;
            let isMailtoLink;
            let isIntegrationLink;
            if (isStringNotEmpty(link_type)) {
                isInternalLink = link_type === "internal";
                isMailtoLink = link_type === "mailto";
                isExternalLink = link_type === "external";
                isIntegrationLink = link_type === "integration";
            } else {
                isInternalLink = isValidGuid(webpage);
                isMailtoLink = isStringNotEmpty(url) && url.startsWith("mailto:");
                isExternalLink = isStringNotEmpty(url) && url.startsWith("https:");
                isIntegrationLink = isStringNotEmpty(url) && url.startsWith(Config.Links.Integration);
            }
            let linkType;
            if (isIntegrationLink) {
                linkType = "integration";
            } else if (isMailtoLink) {
                linkType = "mailto";
            } else if (isInternalLink) {
                linkType = "internal";
            } else {
                linkType = "external";
            }
            const target = isExternalLink ? "_blank" : "_self";
            if (isValidGuid(guid)) {
                
                editor.chain()
                    .focus()
                    .extendMarkRange(markType)
                    .unsetMark(markType)
                    .run();
                
                const _selection = editor?.view?.state?.selection;
                // eslint-disable-next-line no-unused-vars
                const [text, from, to] = getSelectionText(editor);
                setSelection(_selection);
                setSelectedText(text);
                replacementText = replacementText.trim();
                
                const linkData = {
                    href: url,
                    url: url,
                    guid: guid,
                    text: replacementText,
                    link_type: linkType,
                    target: target,
                    webpage: webpage,
                    external: isExternalLink,
                };
                replaceSelection(editor, linkData);
            }
        },
        [editor],
    );

    const getSelectionText = (editor) => {
        const selection = editor?.view?.state?.selection;
        const { $from, $to } = selection;
        const from = $from.pos;
        let to = $to.pos;
        let text = editor?.view?.state?.doc.textBetween(from, to, "");
        const lastChar = text[text.length-1];
        if (lastChar === " ") {
            to = $to.pos -1;
            text = editor?.view?.state?.doc.textBetween(from, to, "");
        }
        return [text, from, to];
    };
    
    const replaceSelection = (editor, markData) => {
        const replacementText = markData?.text;
        const transaction = editor.state.tr;
        const selection = editor?.view?.state?.selection;
        // let { $from, $to } = selection
        const $from = selection?.$from;
        const from = $from.pos;
        const replacementTo = from + replacementText.length;
        transaction.insertText(replacementText);
        const mark = editor.schema.marks.link.create(markData);
        transaction.addMark( from, replacementTo, mark );
        editor.view.dispatch(transaction);
    };
    
    return {
        link,
        updateLink,
        selection,
        selectedText,
        findSelection,
    };
};

